import { useState } from 'react';
import {
  Box, Typography, Chip, Tooltip, TextField, InputAdornment, Button, Badge,
} from '@mui/material';
import {
  Lock as LockIcon,
  Search as SearchIcon,
  FilterAlt as CvFilterIcon,
  AutoAwesome as AiIcon,
  VerifiedUser as BackgroundCheckIcon,
  UploadFile as DocumentsIcon,
} from '@mui/icons-material';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useDroppable } from '@dnd-kit/core';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';
import api from '../../config/api';
import { SortableCandidateCard } from './AtsCandidateCard';
import AtsCvFilteringDialog from './AtsCvFilteringDialog';
import AtsPsycholaboralComparisonDialog from './AtsPsycholaboralComparisonDialog';
import AtsTestSendDialog from './AtsTestSendDialog';
import AtsInterviewDialog from './AtsInterviewDialog';
import AtsPsycholaboralDialog from './AtsPsycholaboralDialog';
import AtsBackgroundCheckDialog from './AtsBackgroundCheckDialog';
import AtsBulkRequestDocumentsDialog from './AtsBulkRequestDocumentsDialog';
import useOrganizationCountry from '../../hooks/useOrganizationCountry';
import { stageHasBackgroundCheck } from './defaultPipeline';
import { BackgroundCheckChargesTip, useCheckPoints } from './backgroundCheckPricing';

const MODULE_CONFIG = {
  disc_test: {
    labelKey: 'ats.modules.discTest',
    color: '#6A5AF9',
    endpoint: 'disc-tests',
    // discOptions viene del selector simple/completo de AtsTestSendDialog.
    // Se envía siempre positionId (vínculo con la vacante, requerido por los
    // listados by-position); en completo se agregan además los datos del cargo
    // de la vacante y los opcionales (cargo actual y competencias).
    buildBody: (app, organization, project, positionId, positionName, positionDescription, lang, selectedTypes, discOptions) => {
      const mode = discOptions?.mode || 'complete';
      const body = {
        applicantId: app.applicantId,
        applicantName: app.candidateName || app.candidateEmail,
        applicantEmail: app.candidateEmail,
        mode,
        projectId: project,
        organizationId: organization,
        positionId,
        language: lang,
        sendEmail: true,
      };
      if (mode === 'complete') {
        body.positionName = positionName;
        body.positionDescription = positionDescription;
        if (discOptions?.currentPosition) {
          body.currentPosition = discOptions.currentPosition;
        }
        if (discOptions?.competencies?.length > 0) {
          body.competencies = discOptions.competencies;
        }
      }
      return body;
    },
  },
  reasoning_test: {
    labelKey: 'ats.modules.reasoningTest',
    color: '#0EA5E9',
    endpoint: 'reasoning-tests/batch',
    buildBody: (app, organization, project, positionId, positionName, positionDescription, lang, selectedTypes) => ({
      applicantId: app.applicantId,
      applicantName: app.candidateName || app.candidateEmail,
      applicantEmail: app.candidateEmail,
      testTypes: selectedTypes,
      projectId: project,
      organizationId: organization,
      positionId,
      positionName,
      language: lang,
      sendEmail: true,
    }),
  },
  personal_skills: {
    labelKey: 'ats.modules.personalSkills',
    color: '#EC4899',
    endpoint: 'reasoning-tests/batch',
    buildBody: (app, organization, project, positionId, positionName, positionDescription, lang, selectedTypes) => ({
      applicantId: app.applicantId,
      applicantName: app.candidateName || app.candidateEmail,
      applicantEmail: app.candidateEmail,
      testTypes: selectedTypes,
      projectId: project,
      organizationId: organization,
      positionId,
      positionName,
      language: lang,
      sendEmail: true,
    }),
  },
  sales_assessment: {
    labelKey: 'ats.modules.salesAssessment',
    color: '#F59E0B',
    endpoint: 'sales-assessments',
    buildBody: (app, organization, project, positionId, positionName, positionDescription, lang) => ({
      applicantId: app.applicantId,
      applicantName: app.candidateName || app.candidateEmail,
      applicantEmail: app.candidateEmail,
      projectId: project,
      organizationId: organization,
      positionId,
      positionName,
      language: lang,
      sendEmail: true,
    }),
  },
};

export { MODULE_CONFIG };

const AtsStageColumn = ({
  stage,
  applications,
  isLocked,
  canEdit = true,
  phaseName,
  onCardClick,
  position,
  positionId,
  selectedApplicationIds = [],
  onSelectionChange,
  onBulkSelectionChange,
  onStageAction,
  cvScores = {},
  onCvScoresRefresh,
  allApplications = [],
  onPositionChange,
  onApplicationPatch,
}) => {
  const { t, i18n } = useTranslation();
  const { authToken, organization, project, showAlert } = useStore.getState();
  const { setNodeRef, isOver } = useDroppable({ id: stage.id, disabled: isLocked });
  const lang = i18n.language === 'en' ? 'en' : 'es';

  const [search, setSearch] = useState('');
  const [cvFilterOpen, setCvFilterOpen] = useState(false);
  const [testDialogModule, setTestDialogModule] = useState(null);
  const [sendingTest, setSendingTest] = useState(false);
  const [sendResults, setSendResults] = useState(null);
  const [interviewDialogOpen, setInterviewDialogOpen] = useState(false);
  const [psychoDialogOpen, setPsychoDialogOpen] = useState(false);
  const [psychoComparisonOpen, setPsychoComparisonOpen] = useState(false);
  const [backgroundCheckOpen, setBackgroundCheckOpen] = useState(false);
  const [documentsOpen, setDocumentsOpen] = useState(false);
  // Módulo "Verificación de antecedentes" (PLAN_MASIVO_PORTAL); también la
  // etapa tipo 'background_check' de las vacantes de prueba previas
  const isBackgroundCheckStage = stageHasBackgroundCheck(stage);
  // Fuera de Chile la verificación está en espera: el botón queda en gris
  const { operatesInChile, loading: orgCountryLoading } = useOrganizationCountry();
  // Precio de la verificación de antecedentes (2026-11-01, PLAN_COBRO_ANTECEDENTES)
  const { points: checkPoints } = useCheckPoints();
  const backgroundCheckUnavailable = orgCountryLoading || !operatesInChile;

  const hasCvFiltering = stage.modules?.includes('cv_filtering');
  const hasInterview = stage.modules?.includes('interview');
  // Preguntas de entrevista del cargo (se definen, con IA o a mano, en el modal de entrevistas)
  const positionQuestionsCount = position?.interviewQuestions?.questions?.length || 0;
  const hasPsicolaboral = stage.modules?.includes('psicolaboral');
  const hasPsychoComparison = stage.modules?.includes('psycholaboral_comparison');
  // Módulo "Solicitud de documentos" (PLAN_SOLICITUD_DOCUMENTOS_MASIVA)
  const hasDocumentRequest = stage.modules?.includes('document_request');
  const testModules = (stage.modules || []).filter(
    (m) => m !== 'cv_filtering' && m !== 'interview' && MODULE_CONFIG[m]
  );

  const filteredApps = applications.filter((a) => {
    if (!search.trim()) return true;
    const term = search.toLowerCase();
    return (
      a.candidateName?.toLowerCase().includes(term) ||
      a.candidateEmail?.toLowerCase().includes(term)
    );
  });

  const filteredAppIds = filteredApps.map((application) => application.uid || application.id);
  const selectedVisibleCount = filteredAppIds.filter((id) => selectedApplicationIds.includes(id)).length;
  const allVisibleSelected = filteredAppIds.length > 0 && selectedVisibleCount === filteredAppIds.length;

  const handleToggleVisibleSelection = () => {
    if (!onBulkSelectionChange) return;
    onBulkSelectionChange(filteredApps, !allVisibleSelected);
  };

  const handleSendBulk = async (selectedCandidateIds, selectedTypes = null, discOptions = null) => {
    if (!testDialogModule) return;
    setSendingTest(true);

    const config = MODULE_CONFIG[testDialogModule];
    const selected = applications.filter((a) => selectedCandidateIds.includes(a.uid || a.id));

    let sent = 0;
    let failed = 0;

    for (const app of selected) {
      if (!app.applicantId) { failed++; continue; }
      try {
        const body = config.buildBody(
          app,
          organization,
          project,
          positionId,
          position?.name,
          position?.descripcion || '',
          lang,
          selectedTypes,
          discOptions
        );
        await api.post(
          `/${config.endpoint}`,
          body,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
        sent++;
      } catch (err) {
        console.error(err);
        failed++;
      }
    }

    setSendingTest(false);
    setSendResults({ sent, failed });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 200, maxWidth: 220 }}>

      {/* Header etapa */}
      <Box sx={{
        px: 1.5, py: 1,
        backgroundColor: isLocked ? '#F3F4F6' : '#EDE9FE',
        borderRadius: '8px 8px 0 0',
        borderBottom: `2px solid ${isLocked ? '#E5E7EB' : '#6A5AF9'}`,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="caption"
              fontWeight={700}
              color={isLocked ? 'text.disabled' : '#4C1D95'}
              sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {stage.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
              {phaseName}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            <Chip
              label={applications.length}
              size="small"
              sx={{
                height: 18, fontSize: '0.65rem',
                backgroundColor: isLocked ? '#E5E7EB' : '#6A5AF9',
                color: isLocked ? '#9CA3AF' : '#fff',
              }}
            />
            {isLocked && (
              <Tooltip title={t('ats.pipeline.lockedStage')}>
                <LockIcon sx={{ fontSize: 14, color: '#9CA3AF' }} />
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Buscador */}
        {!isLocked && applications.length > 0 && (
          <TextField
            size="small"
            placeholder={t('ats.pipeline.searchInStage')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              mt: 1, width: '100%',
              '& .MuiInputBase-root': { fontSize: '0.75rem', backgroundColor: '#fff', borderRadius: 1 },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 14 }} />
                </InputAdornment>
              ),
            }}
          />
        )}

        {canEdit && !isLocked && filteredApps.length > 0 && (
          <Button
            size="small"
            variant="text"
            onClick={handleToggleVisibleSelection}
            sx={{
              mt: 1,
              width: '100%',
              fontSize: '0.7rem',
              color: '#6A5AF9',
            }}
          >
            {allVisibleSelected ? 'Limpiar visibles' : 'Seleccionar visibles'}
          </Button>
        )}

        {/* Botón Verificar antecedentes (módulo de la etapa). Quien no puede
            editar la vacante igual ve el estado; verificar exige edición. */}
        {isBackgroundCheckStage && !isLocked && (
          <>
            <Tooltip
              title={backgroundCheckUnavailable
                ? (orgCountryLoading ? '' : t('ats.backgroundCheckStage.chileOnly'))
                : <BackgroundCheckChargesTip auto={Boolean(stage.backgroundCheckAuto)} />}
              arrow
              describeChild
            >
              <span style={{ display: 'block' }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<BackgroundCheckIcon sx={{ fontSize: 14 }} />}
                  onClick={() => setBackgroundCheckOpen(true)}
                  disabled={backgroundCheckUnavailable}
                  sx={{
                    mt: 1, width: '100%', fontSize: '0.7rem',
                    borderColor: '#B45309', color: '#B45309',
                    '&:hover': { borderColor: '#92400E', backgroundColor: '#FFFBEB' },
                  }}
                >
                  {t('ats.backgroundCheckStage.button')}
                </Button>
              </span>
            </Tooltip>
            {/* Consulta automática al llegar a la columna (switch del engranaje) */}
            {stage.backgroundCheckAuto && !backgroundCheckUnavailable && (
              <Tooltip title={t('ats.backgroundCheckStage.autoOnHint', { points: checkPoints })} arrow>
                <Typography
                  variant="caption"
                  sx={{ display: 'block', textAlign: 'center', mt: 0.25, fontSize: '0.65rem', fontWeight: 600, color: '#B45309' }}
                >
                  {t('ats.backgroundCheckStage.autoOn')}
                </Typography>
              </Tooltip>
            )}
          </>
        )}

        {/* Botón CV Filtering */}
        {canEdit && hasCvFiltering && !isLocked && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<CvFilterIcon sx={{ fontSize: 14 }} />}
            onClick={() => setCvFilterOpen(true)}
            sx={{
              mt: 1, width: '100%', fontSize: '0.7rem',
              borderColor: '#10B981', color: '#10B981',
              '&:hover': { borderColor: '#059669', backgroundColor: '#F0FDF4' },
            }}
          >
            {t('ats.modules.cvFiltering')}
          </Button>
        )}

        {/* Botones de test */}
        {canEdit && !isLocked && testModules.map((moduleId) => {
          const config = MODULE_CONFIG[moduleId];
          return (
            <Button
              key={moduleId}
              size="small"
              variant="outlined"
              onClick={() => { setTestDialogModule(moduleId); setSendResults(null); }}
              sx={{
                mt: 1, width: '100%', fontSize: '0.7rem',
                borderColor: config.color, color: config.color,
                '&:hover': { borderColor: config.color, backgroundColor: `${config.color}11` },
              }}
            >
              {t(config.labelKey)}
            </Button>
          );
        })}

        {/* Botón Solicitar documentos: estado de la etapa y solicitud masiva */}
        {canEdit && hasDocumentRequest && !isLocked && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<DocumentsIcon sx={{ fontSize: 14 }} />}
            onClick={() => setDocumentsOpen(true)}
            sx={{
              mt: 1, width: '100%', fontSize: '0.7rem',
              borderColor: '#2563EB', color: '#2563EB',
              '&:hover': { borderColor: '#1D4ED8', backgroundColor: '#EFF6FF' },
            }}
          >
            {t('ats.documents.bulk.columnButton')}
          </Button>
        )}

        {/* Botón Entrevistas: además de registrar entrevistas, desde su modal
            se definen las preguntas del cargo (con IA o a mano). El tooltip lo
            explica y el punto avisa mientras el cargo aún no tiene preguntas. */}
        {canEdit && hasInterview && !isLocked && (
          <Tooltip
            arrow
            placement="top"
            title={positionQuestionsCount > 0
              ? t('ats.interviewQuestions.columnTooltipReady', { count: positionQuestionsCount })
              : t('ats.interviewQuestions.columnTooltipEmpty')}
          >
            <Badge
              variant="dot"
              invisible={positionQuestionsCount > 0}
              sx={{
                mt: 1, width: '100%', display: 'flex',
                '& .MuiBadge-dot': { backgroundColor: '#7C3AED' },
              }}
            >
              <Button
                size="small"
                variant="outlined"
                startIcon={<AiIcon sx={{ fontSize: 14 }} />}
                onClick={() => setInterviewDialogOpen(true)}
                sx={{
                  width: '100%', fontSize: '0.7rem',
                  borderColor: '#8B5CF6', color: '#8B5CF6',
                  '&:hover': { borderColor: '#7C3AED', backgroundColor: '#F5F3FF' },
                }}
              >
                {t('ats.modules.interview')}
              </Button>
            </Badge>
          </Tooltip>
        )}

        {/* Botón Psicolaboral */}
        {canEdit && hasPsicolaboral && !isLocked && (
          <Button
            size="small"
            variant="outlined"
            onClick={() => setPsychoDialogOpen(true)}
            sx={{
              mt: 1, width: '100%', fontSize: '0.7rem',
              borderColor: '#EC4899', color: '#EC4899',
              '&:hover': { borderColor: '#DB2777', backgroundColor: '#FDF2F8' },
            }}
          >
            {t('ats.modules.psicolaboral')}
          </Button>
        )}

        {/* Botón Ranking psicolaboral (equivale a Comparación Evaluaciones del legacy) */}
        {canEdit && hasPsychoComparison && !isLocked && (
          <Tooltip title={t('ats.modules.psychoComparisonTooltip')} placement="top">
            <Button
              size="small"
              variant="outlined"
              onClick={() => setPsychoComparisonOpen(true)}
              sx={{
                mt: 1, width: '100%', fontSize: '0.7rem',
                borderColor: '#0D9488', color: '#0D9488',
                '&:hover': { borderColor: '#0F766E', backgroundColor: '#F0FDFA' },
              }}
            >
              {t('ats.modules.psychoComparison')}
            </Button>
          </Tooltip>
        )}
      </Box>

      {/* Área droppable */}
      <Box
        ref={setNodeRef}
        sx={{
          flex: 1, minHeight: 300, p: 1,
          backgroundColor: isOver && !isLocked ? '#F5F3FF' : '#F9FAFB',
          border: '1px solid #E5E7EB', borderTop: 'none',
          borderRadius: '0 0 8px 8px',
          display: 'flex', flexDirection: 'column', gap: 1,
          transition: 'background-color 0.15s ease',
          ...(isLocked && { opacity: 0.5, cursor: 'not-allowed' }),
        }}
      >
        <SortableContext
          items={filteredApps.map((a) => a.uid || a.id)}
          strategy={verticalListSortingStrategy}
        >
          {filteredApps.map((application) => (
            <SortableCandidateCard
              key={application.uid || application.id}
              application={application}
              // Con los visibles de la columna en su orden (buscador y filtro
              // de revisión incluidos): con ellos se arma el recorrido de la ficha
              onCardClick={(app) => onCardClick(app, filteredAppIds)}
              selectable={canEdit && !isLocked}
              isSelected={selectedApplicationIds.includes(application.uid || application.id)}
              onSelectionChange={onSelectionChange}
              stageType={stage.type}
              onStageAction={onStageAction}
              canAct={canEdit && !isLocked}
              cvScore={application.applicantId != null ? cvScores[application.applicantId] ?? null : null}
            />
          ))}
        </SortableContext>

        {filteredApps.length === 0 && !isLocked && (
          <Box sx={{
            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '1px dashed #D1D5DB', borderRadius: 1, minHeight: 60,
          }}>
            <Typography variant="caption" color="text.disabled">
              {search ? t('ats.pipeline.noResults') : t('ats.pipeline.dropHere')}
            </Typography>
          </Box>
        )}
      </Box>

      {/* CV Filtering Dialog */}
      <AtsCvFilteringDialog
        open={cvFilterOpen}
        onClose={() => {
          setCvFilterOpen(false);
          if (onCvScoresRefresh) onCvScoresRefresh();
        }}
        stage={stage}
        applications={applications}
        position={position}
        positionId={positionId}
      />

      {/* Test Send Dialog */}
      <AtsTestSendDialog
        open={!!testDialogModule}
        onClose={() => { setTestDialogModule(null); setSendResults(null); }}
        moduleId={testDialogModule}
        moduleConfig={testDialogModule ? MODULE_CONFIG[testDialogModule] : null}
        applications={applications}
        positionId={positionId}
        positionCompetencies={position?.competencias}
        onSendBulk={handleSendBulk}
        sending={sendingTest}
        sendResults={sendResults}
      />

      {/* Interview Dialog */}
      <AtsInterviewDialog
        open={interviewDialogOpen}
        onClose={() => setInterviewDialogOpen(false)}
        applications={applications}
        stage={stage}
        positionId={positionId}
        position={position}
        onPositionChange={onPositionChange}
      />

      {/* Psicolaboral Dialog */}
      <AtsPsycholaboralDialog
        open={psychoDialogOpen}
        onClose={() => setPsychoDialogOpen(false)}
        position={position}
        positionId={positionId}
        applications={applications}
      />

      {/* Verificación de antecedentes Dialog (solo en la etapa con el módulo) */}
      {isBackgroundCheckStage && (
        <AtsBackgroundCheckDialog
          open={backgroundCheckOpen}
          onClose={() => setBackgroundCheckOpen(false)}
          stage={stage}
          applications={applications}
          positionId={positionId}
          canEdit={canEdit}
          onApplicationPatch={onApplicationPatch}
          onOpenApplication={onCardClick}
        />
      )}

      {/* Solicitud de documentos Dialog (solo en la etapa con el módulo) */}
      {hasDocumentRequest && (
        <AtsBulkRequestDocumentsDialog
          open={documentsOpen}
          onClose={() => setDocumentsOpen(false)}
          mode="column"
          stageName={stage.name}
          applications={applications}
          positionId={positionId}
          onApplicationPatch={onApplicationPatch}
          onOpenApplication={onCardClick}
        />
      )}

      {/* Ranking psicolaboral Dialog — opera sobre todos los candidatos de la
          vacante con evaluación psicolaboral, no solo los de esta etapa */}
      <AtsPsycholaboralComparisonDialog
        open={psychoComparisonOpen}
        onClose={() => setPsychoComparisonOpen(false)}
        positionId={positionId}
        allApplications={allApplications}
      />

    </Box>
  );
};

export default AtsStageColumn;